import type { CogoNavigationGuard } from "../core/types"

export interface GuardResult {
  allow: boolean
  redirect?: string
}

/** Role-based guard from NavigationData.guards */
export interface RoleGuard {
  route: string
  roles: string[]
  redirect: string
}

const DEFAULT_TIMEOUT = 5000

export class NavigationGuardRunner {
  private roleGuards: RoleGuard[] = []

  constructor(
    private guards: CogoNavigationGuard[],
    private executeFlow: (flowName: string, context: Record<string, unknown>) => Promise<void>,
    private getState: (path: string) => unknown,
  ) {}

  /** Add a role-based guard from NavigationData */
  addRoleGuard(guard: RoleGuard): void {
    this.roleGuards.push(guard)
  }

  async check(targetRoute: string, currentRoute: string): Promise<GuardResult> {
    // Check role-based guards first (from NavigationData.guards)
    for (const rg of this.roleGuards) {
      if (NavigationGuardRunner.matchPattern(rg.route, targetRoute)) {
        const userRoles = this.getState("_system.auth.roles") as string[] | undefined
        if (!userRoles || !rg.roles.some((r) => userRoles.includes(r))) {
          return { allow: false, redirect: rg.redirect }
        }
      }
    }

    // Flow-based guards
    const matching = this.guards.filter((g) =>
      NavigationGuardRunner.matchPattern(g.pattern, targetRoute),
    )

    if (matching.length === 0) {
      return { allow: true }
    }

    for (const guard of matching) {
      const timeout = guard.timeout ?? DEFAULT_TIMEOUT
      const context = { targetRoute, currentRoute }

      try {
        await Promise.race([
          this.executeFlow(guard.flow, context),
          rejectAfter(timeout),
        ])
      } catch {
        // Timeout — deny
        return { allow: false }
      }

      const allow = this.getState("_guard.allow")
      if (!allow) {
        const redirect = this.getState("_guard.redirect") as string | undefined
        return { allow: false, ...(redirect != null ? { redirect } : {}) }
      }
    }

    return { allow: true }
  }

  static matchPattern(pattern: string, route: string): boolean {
    if (!pattern || !route) return false

    if (pattern === route) {
      return true
    }

    if (pattern.endsWith("/*")) {
      const prefix = pattern.slice(0, -2) // remove "/*"
      return route.startsWith(prefix + "/")
    }

    return false
  }
}

function rejectAfter(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error("Guard timeout")), ms)
  })
}
